import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  MenuItem,
  FormControl,
  Select,
  Typography,
  useTheme,
  IconButton,
  Button,
  Menu,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Visibility,
  KeyboardArrowDown,
} from "@mui/icons-material";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import CreateShiftModal from "./CreateShiftModal";
import ViewShiftModal from "./ViewShiftModal";
import ShiftCalendar from "./ShiftCalendar";
import { useTranslation } from "react-i18next";
import {
  format,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
} from "date-fns";
import { shiftAssignmentSchema } from "../../config/forms/shiftAssignmentSchema";
import {
  VIEW_MODES,
  FILTER_OPTIONS,
  DATE_FORMATS,
} from "../../utils/Constants";
const styles = {
  container: {
    p: { xs: 1.5, sm: 3 },
    bgcolor: "#fff",
    height: "100%",
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: { xs: 2, sm: 3 },
    flexDirection: { xs: "column", sm: "row" },
    width: { xs: "100%", sm: "auto" },
  },
  filterControl: {
    minWidth: { xs: "100%", sm: 220 },
  },
  smallFilterControl: {
    minWidth: { xs: "100%", sm: 150 },
  },
  placeholderText: {
    color: "#9E9E9E",
    fontWeight: 500,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
  },
  selectedText: {
    color: "#333",
    fontWeight: 600,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
  },
  select: {
    borderRadius: "8px",
    bgcolor: "#fff",
    ".MuiSelect-select": {
      py: 1.5,
      pl: 2,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#F7A86E",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#F7A86E",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#F7A86E",
    },
    boxShadow: "0px 2px 5px rgba(0,0,0,0.02)",
  },
  menuPaper: {
    borderRadius: 2,
    mt: 1,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    "& .MuiMenuItem-root": {
      fontSize: { xs: "0.8rem", sm: "0.9rem" },
      fontWeight: 500,
      py: 1.5,
      "&:hover": {
        bgcolor: "#FFF8E1",
        color: "#F7A86E",
      },
      "&.Mui-selected": {
        bgcolor: "#FFF8E1 !important",
        color: "#F7A86E",
        fontWeight: 700,
      },
    },
  },
  menuItem: {
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
  },
  navigator: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    bgcolor: "#F5F5F5",
    borderRadius: "8px",
    p: 0.5,
    border: "1px solid #E0E0E0",
  },
  dateLabel: {
    fontWeight: 600,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
    color: "#333",
    px: 1,
  },
  iconButton: { color: "#333" },
  weekButton: (isActive) => ({
    minWidth: 60,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    height: 36,
    ml: 1,
    bgcolor: isActive ? "#F7A86E" : "#f5f5f5",
    color: isActive ? "#fff" : "#757575",
    border: isActive ? "none" : "1px solid #E0E0E0",
    boxShadow: isActive ? "0 2px 5px rgba(247, 168, 110, 0.4)" : "none",
    "&:hover": {
      bgcolor: isActive ? "#E2955E" : "#eeeeee",
    },
  }),
  actionButtons: {
    display: "flex",
    gap: 1,
    ml: { xs: 0, sm: "auto" },
    width: { xs: "100%", sm: "auto" },
    justifyContent: { xs: "center", sm: "flex-start" },
    mr: { xs: 0, sm: 2 },
    mt: { xs: 2, sm: 0 },
    flexWrap: "wrap",
  },
  shiftButton: {
    background: "linear-gradient(90deg, #E2A532 0%, #F79072 100%)",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    px: 4,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(247, 144, 114, 0.3)",
    },
  },
  shiftMenuPaper: {
    mt: 1,
    minWidth: 150,
    borderRadius: 2,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    "& .MuiMenuItem-root": {
      fontSize: "0.9rem",
      fontWeight: 500,
      py: 1.5,
      color: "#333",
      "&:hover": {
        bgcolor: "#FFF8E1",
        color: "#F7A86E",
      },
    },
  },
};
const EmployeeShift = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { api } = ApiClient();
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState(FILTER_OPTIONS.ALL);
  const [shiftFilter, setShiftFilter] = useState(FILTER_OPTIONS.ALL);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(VIEW_MODES.WEEK);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [shiftMenuAnchor, setShiftMenuAnchor] = useState(null);
  const handleShiftMenuOpen = (event) => {
    setShiftMenuAnchor(event.currentTarget);
  };
  const handleShiftMenuClose = () => {
    setShiftMenuAnchor(null);
  };
  useEffect(() => {
    fetchEmployees();
    fetchShiftTemplates();
  }, []);
  useEffect(() => {
    fetchAssignments();
  }, [currentDate, view]);
  const fetchEmployees = async () => {
    try {
      const response = await api.get(END_POINT.EMPLOYEE.GET_ALL_EMPLOYEES);
      if (response.data && response.data.data) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch employees", error);
    }
  };
  const fetchShiftTemplates = async () => {
    try {
      const response = await api.get(END_POINT.EMPLOYEE.GET_ALL_SHIFTS);
      if (response.data && response.data.data) {
        setShiftTemplates(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch shift templates", error);
    }
  };
  const fetchAssignments = async () => {
    try {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      const startDate = format(start, DATE_FORMATS.API_DATE);
      const endDate = format(end, DATE_FORMATS.API_DATE);
      const params = {};
      if (view === VIEW_MODES.WEEK) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const response = await api.get(
        END_POINT.EMPLOYEE.GET_SHIFT_ASSIGNMENTS,
        params
      );
      if (response.data && response.data.data) {
        setAssignments(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch shift assignments", error);
    }
  };
  const handleCreateTemplate = async (shiftData) => {
    try {
      const response = await api.post(
        END_POINT.EMPLOYEE.CREATE_SHIFT,
        shiftData
      );
      if (response.data.status || response.status === 200) {
        const newShift = response.data.data || shiftData;
        setShiftTemplates((prev) => [...prev, newShift]);
        setIsModalOpen(false);
      } else {
        console.error("Failed to create shift template:", response.data.msg);
      }
    } catch (error) {
      console.error("Error creating shift template:", error);
    }
  };
  const handleAssignShift = async (assignmentData) => {
    try {
      console.log("handleAssignShift received:", assignmentData);
      const existing = assignments.find(
        (a) =>
          a.employee_id === assignmentData.employee_id &&
          format(new Date(a.date), DATE_FORMATS.API_DATE) ===
            format(new Date(assignmentData.date), DATE_FORMATS.API_DATE)
      );
      if (existing) {
        console.warn("Assignment blocked: Shift already exists on this date.");
        return;
      }
      const payload = {
        employee_id: assignmentData.employee_id,
        date: assignmentData.date,
        shift_id: assignmentData.shift_id || assignmentData.id,
      };
      console.log("handleAssignShift constructed payload:", payload);
      const validData = await shiftAssignmentSchema.validate(payload, {
        stripUnknown: true,
        abortEarly: false,
      });
      const response = await api.post(
        END_POINT.EMPLOYEE.CREATE_SHIFT_ASSIGNMENT,
        validData
      );
      if (response.data.status || response.status === 200) {
        const newAssignment = response.data.data || assignmentData;
        setAssignments((prev) => [...prev, newAssignment]);
        fetchAssignments();
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        console.error("Validation Error:", error.errors);
      } else {
        console.error("Error assigning shift:", error);
      }
    }
  };
  const handleDeleteAssignment = async (keys) => {
    if (!keys) return;
    const keyArray = Array.isArray(keys) ? keys : [keys];
    try {
      await Promise.all(
        keyArray.map((key) =>
          api.delet(`${END_POINT.EMPLOYEE.DELETE_SHIFT_ASSIGNMENT}/${key}`)
        )
      );
      setAssignments((prev) =>
        prev.filter((s) => !keyArray.includes(s.assignment_key))
      );
    } catch (error) {
      console.error("Failed to delete assignment(s)", error);
    }
  };
  const handleDeleteTemplate = async (id) => {
    if (!id) return;
    try {
      const response = await api.delet(
        `${END_POINT.EMPLOYEE.DELETE_SHIFT}/${id}`
      );
      if (response.status === 200 || response.data.status) {
        setShiftTemplates((prev) => prev.filter((s) => s.shift_key !== id));
      }
    } catch (error) {
      console.error("Failed to delete template", error);
    }
  };
  const handleShiftResizeAction = async ({
    employeeId,
    shiftType,
    currentStart,
    currentEnd,
    newStart,
    newEnd,
    template,
  }) => {
    const newDateMap = new Set(
      eachDayOfInterval({ start: newStart, end: newEnd }).map((d) =>
        format(d, DATE_FORMATS.API_DATE)
      )
    );
    const oldDateMap = new Set(
      eachDayOfInterval({ start: currentStart, end: currentEnd }).map((d) =>
        format(d, DATE_FORMATS.API_DATE)
      )
    );
    const toAdd = [];
    const toDelete = [];
    newDateMap.forEach((dateStr) => {
      if (!oldDateMap.has(dateStr)) {
        toAdd.push(dateStr);
      }
    });
    const hasConflict = toAdd.some((dateStr) => {
      return assignments.some(
        (a) =>
          a.employee_id === employeeId &&
          format(new Date(a.date), DATE_FORMATS.API_DATE) === dateStr
      );
    });
    if (hasConflict) {
      console.warn("Resize blocked: Overlaps with existing shift.");
      fetchAssignments();
      return;
    }
    const relevantAssignments = assignments.filter(
      (a) => a.employee_id === employeeId && a.shift.shift_type === shiftType
    );
    oldDateMap.forEach((dateStr) => {
      if (!newDateMap.has(dateStr)) {
        const assignment = relevantAssignments.find(
          (a) => format(new Date(a.date), DATE_FORMATS.API_DATE) === dateStr
        );
        if (assignment) {
          toDelete.push(assignment.assignment_key);
        }
      }
    });
    if (toDelete.length > 0) {
      await Promise.all(
        toDelete.map((key) =>
          api.delet(`${END_POINT.EMPLOYEE.DELETE_SHIFT_ASSIGNMENT}/${key}`)
        )
      );
    }
    let addedAssignments = [];
    if (toAdd.length > 0) {
      const promises = toAdd.map((dateStr) => {
        const newAssignment = {
          employee_id: employeeId,
          date: dateStr,
          shift_id: template.shift_id || template.shift?.id,
        };
        return api
          .post(END_POINT.EMPLOYEE.CREATE_SHIFT_ASSIGNMENT, newAssignment)
          .then((res) =>
            res.data.data
              ? res.data.data
              : { ...newAssignment, assignment_key: "temp" }
          )
          .catch((err) => {
            console.error(err);
            return null;
          });
      });
      const results = await Promise.all(promises);
      addedAssignments = results.filter((r) => r !== null);
    }
    setAssignments((prev) => {
      const remaining = prev.filter(
        (a) => !toDelete.includes(a.assignment_key)
      );
      return [...remaining, ...addedAssignments];
    });
  };
  const usedColors = useMemo(
    () => shiftTemplates.map((s) => s.color),
    [shiftTemplates]
  );
  const existingShiftTypes = useMemo(
    () => [...new Set(shiftTemplates.map((s) => s.shift_type))],
    [shiftTemplates]
  );
  const processedAssignments = useMemo(() => {
    if (!assignments.length) return [];
    const sorted = [...assignments].sort((a, b) => {
      if (a.employee_id !== b.employee_id) return a.employee_id - b.employee_id;
      return new Date(a.date) - new Date(b.date);
    });
    const withRanges = sorted.map((assignment) => {
      const assignmentDate = new Date(assignment.date);

      let startDate = assignmentDate;
      return { ...assignment, dateObj: assignmentDate };
    });
    const grouped = [];
    let currentGroup = [];
    withRanges.forEach((curr, index) => {
      const prev = withRanges[index - 1];
      const isConsecutive =
        prev &&
        prev.employee_id === curr.employee_id &&
        prev.shift?.shift_type === curr.shift?.shift_type &&
        curr.dateObj.getTime() - prev.dateObj.getTime() === 86400000;
      if (isConsecutive) {
        currentGroup.push(curr);
      } else {
        if (currentGroup.length > 0) {
          grouped.push(currentGroup);
        }
        currentGroup = [curr];
      }
    });
    if (currentGroup.length > 0) grouped.push(currentGroup);
    const finalAssignments = [];
    grouped.forEach((group) => {
      const start = group[0].date;
      const end = group[group.length - 1].date;
      const rangeLabel =
        start === end
          ? format(new Date(start), DATE_FORMATS.DISPLAY_DATE)
          : `${format(new Date(start), DATE_FORMATS.DISPLAY_DATE)} - ${format(
              new Date(end),
              DATE_FORMATS.DISPLAY_DATE
            )}`;
      group.forEach((item) => {
        finalAssignments.push({ ...item, dateRangeLabel: rangeLabel });
      });
    });
    return finalAssignments;
  }, [assignments]);
  const filteredAssignments = useMemo(() => {
    if (shiftFilter === FILTER_OPTIONS.ALL) return processedAssignments;
    return processedAssignments.filter(
      (s) => s.shift?.shift_type === shiftFilter
    );
  }, [processedAssignments, shiftFilter]);
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
      setCurrentDate(new Date());
    }
  };
  const handlePrev = () => {
    if (view === VIEW_MODES.WEEK) setCurrentDate(subWeeks(currentDate, 1));
    else if (view === VIEW_MODES.MONTH)
      setCurrentDate(subMonths(currentDate, 1));
  };
  const handleNext = () => {
    if (view === VIEW_MODES.WEEK) setCurrentDate(addWeeks(currentDate, 1));
    else if (view === VIEW_MODES.MONTH)
      setCurrentDate(addMonths(currentDate, 1));
  };
  const dateLabel = useMemo(() => {
    if (view === VIEW_MODES.WEEK) {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      if (
        format(start, DATE_FORMATS.MONTH) === format(end, DATE_FORMATS.MONTH)
      ) {
        return `${format(start, DATE_FORMATS.DAY)}-${format(
          end,
          DATE_FORMATS.DAY
        )} ${format(end, DATE_FORMATS.DISPLAY_MONTH)}`;
      }
      return `${format(start, DATE_FORMATS.DISPLAY_DATE)} - ${format(
        end,
        DATE_FORMATS.DISPLAY_DATE_FULL
      )}`;
    }
    if (view === VIEW_MODES.MONTH)
      return format(currentDate, DATE_FORMATS.DISPLAY_MONTH);
    return "";
  }, [view, currentDate]);
  const filteredEmployees = useMemo(() => {
    if (employeeFilter === FILTER_OPTIONS.ALL) return employees;
    return employees.filter((e) => e.employee_id === employeeFilter);
  }, [employees, employeeFilter]);
  return (
    <Box sx={styles.container}>
      <Box sx={styles.filterBar}>
        <FormControl sx={styles.filterControl} size="small">
          <Select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (selected === FILTER_OPTIONS.ALL) {
                return (
                  <Typography sx={styles.placeholderText}>
                    {t("pages.shift_management.employee_name")}
                  </Typography>
                );
              }
              const emp = employees.find((e) => e.employee_id === selected);
              return (
                <Typography sx={styles.selectedText}>
                  {emp ? emp.name : selected}
                </Typography>
              );
            }}
            sx={styles.select}
            MenuProps={{ PaperProps: { sx: styles.menuPaper } }}
          >
            <MenuItem value={FILTER_OPTIONS.ALL} sx={styles.menuItem}>
              {t("pages.shift_management.all_employees")}
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.employee_id} value={emp.employee_id}>
                {emp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={styles.smallFilterControl} size="small">
          <Select
            value={shiftFilter}
            onChange={(e) => setShiftFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (selected === FILTER_OPTIONS.ALL) {
                return (
                  <Typography sx={styles.placeholderText}>
                    {t("pages.shift_management.shift_type")}
                  </Typography>
                );
              }
              return (
                <Typography sx={styles.selectedText}>{selected}</Typography>
              );
            }}
            sx={styles.select}
            MenuProps={{ PaperProps: { sx: styles.menuPaper } }}
          >
            <MenuItem value={FILTER_OPTIONS.ALL} sx={styles.menuItem}>
              {t("pages.shift_management.all_shifts")}
            </MenuItem>
            {existingShiftTypes.map((type) => (
              <MenuItem key={type} value={type} sx={styles.menuItem}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={styles.navigator}>
          <IconButton size="small" sx={styles.iconButton} onClick={handlePrev}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography sx={styles.dateLabel}>{dateLabel}</Typography>
          <IconButton size="small" sx={styles.iconButton} onClick={handleNext}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
        <Button
          onClick={() => {
            setView(VIEW_MODES.WEEK);
            setCurrentDate(new Date());
          }}
          sx={styles.weekButton(view === VIEW_MODES.WEEK)}
        >
          {t("pages.shift_management.week") || "Week"}
        </Button>
        <Box sx={styles.actionButtons}>
          <Button
            endIcon={<KeyboardArrowDown />}
            variant="contained"
            onClick={handleShiftMenuOpen}
            sx={styles.shiftButton}
          >
            {t("pages.shift_management.shift") || "Shift"}
          </Button>
          <Menu
            anchorEl={shiftMenuAnchor}
            open={Boolean(shiftMenuAnchor)}
            onClose={handleShiftMenuClose}
            PaperProps={{ sx: styles.shiftMenuPaper }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={() => {
                handleShiftMenuClose();
                setIsModalOpen(true);
              }}
            >
              <Add fontSize="small" sx={{ mr: 1 }} />{" "}
              {t("pages.shift_management.create_shift")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleShiftMenuClose();
                setIsViewModalOpen(true);
              }}
            >
              <Visibility fontSize="small" sx={{ mr: 1 }} />{" "}
              {t("pages.shift_management.view_shift") || "View Shift"}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <ShiftCalendar
        view={view}
        currentDate={currentDate}
        employees={filteredEmployees}
        shifts={filteredAssignments}
        shiftTemplates={shiftTemplates}
        onAssignShift={handleAssignShift}
        onDeleteShift={handleDeleteAssignment}
        onShiftResize={handleShiftResizeAction}
        onVisibleDateChange={setCurrentDate}
      />
      <CreateShiftModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreateTemplate}
        usedColors={usedColors}
        existingShiftTypes={existingShiftTypes}
      />
      <ViewShiftModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        shifts={shiftTemplates}
        onDelete={handleDeleteTemplate}
      />
    </Box>
  );
};

export default EmployeeShift;
